import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { coursesAPI, examsAPI, uploadsAPI, enrollmentAPI, progressAPI } from "../services/api";
import StudentProgress from "../components/StudentProgress";
import ExamReviewModal from "../components/ExamReviewModal";
import Announcements from "./Announcements";
import { toast } from "react-toastify";
import { FILE_BASE_URL } from "../config";
import "../styles/CourseDetails.css";
import {
  FaBookOpen,
  FaChalkboardTeacher,
  FaClipboardList,
  FaUsers,
  FaPlus,
  FaTrash,
  FaFilePdf,
  FaFileVideo,
  FaFileAlt,
  FaCheck,
  FaTimes,
  FaBan,
  FaEdit,
  FaEye,
  FaChartLine,
  FaCopy,
  FaSearch,
  FaCheckSquare,
  FaSquare,
  FaUserCheck,
  FaUserClock,
  FaUserTimes,
  FaExclamationTriangle,
  FaPen,
  FaShareAlt
} from "react-icons/fa";

const CourseDetails = () => {
  const { courseId } = useParams();
  const navigate = useNavigate();

  const [course, setCourse] = useState(null);
  const [lessons, setLessons] = useState([]);
  const [exams, setExams] = useState([]);

  // Enrollment
  const [pendingStudents, setPendingStudents] = useState([]);
  const [enrolledStudents, setEnrolledStudents] = useState([]);
  const [loadingStudents, setLoadingStudents] = useState(false);

  // Students tab UI state
  const [studentFilter, setStudentFilter] = useState("all"); // all | pending | enrolled
  const [studentSearch, setStudentSearch] = useState("");
  const [selectedPending, setSelectedPending] = useState([]); // ids for bulk actions
  const [copySuccess, setCopySuccess] = useState(false);
  const [reviewStudent, setReviewStudent] = useState(null); // { id, name } for exam review modal

  const [activeTab, setActiveTab] = useState("overview");
  const [loading, setLoading] = useState(true);

  // Pending Reviews (essay questions)
  const [pendingReviews, setPendingReviews] = useState([]);
  const [loadingReviews, setLoadingReviews] = useState(false);

  useEffect(() => {
    const fetchCourseDetails = async () => {
      const authToken = localStorage.getItem("authToken");
      if (!authToken) {
        navigate("/login");
        return;
      }

      try {
        const coursesResponse = await coursesAPI.getAll();
        const courseData = coursesResponse.data.find((c) => c.id === Number(courseId));
        if (!courseData) throw new Error("❌ الدورة غير موجودة.");

        setCourse({
          id: courseData.id,
          name: courseData.title,
          description: courseData.description,
          access_code: courseData.access_code,
          created_at: courseData.created_at,
          enrollment_open: courseData.enrollment_open
        });

        // Lectures
        try {
          const lessonsResponse = await uploadsAPI.getLectures(courseId);
          setLessons(lessonsResponse.data);
        } catch (e) {
          console.warn("Failed to fetch lessons", e);
        }

        // Exams
        try {
          const examsResponse = await examsAPI.getByCourse(courseId);
          setExams(examsResponse.data);
        } catch (e) {
          console.warn("Failed to fetch exams", e);
        }
      } catch (error) {
        console.error("❌ حدث خطأ أثناء جلب تفاصيل الدورة:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchCourseDetails();
  }, [courseId, navigate]);

  const fetchStudents = useCallback(async () => {
    setLoadingStudents(true);
    try {
      const [pendingRes, approvedRes] = await Promise.all([
        enrollmentAPI.getEnrollments(courseId, "pending"),
        enrollmentAPI.getEnrollments(courseId, "approved")
      ]);
      setPendingStudents(pendingRes.data);
      setEnrolledStudents(approvedRes.data);
      setSelectedPending([]);
    } catch (error) {
      console.error("Failed to fetch students:", error);
    } finally {
      setLoadingStudents(false);
    }
  }, [courseId]);

  const fetchPendingReviews = useCallback(async () => {
    setLoadingReviews(true);
    try {
      const { data } = await progressAPI.getPendingReviews(courseId);
      setPendingReviews(data);
    } catch (e) {
      console.error("Failed to fetch pending reviews", e);
    } finally {
      setLoadingReviews(false);
    }
  }, [courseId]);

  // Fetch data on course load
  useEffect(() => {
    if (course) {
      fetchStudents();
      fetchPendingReviews();
    }
  }, [course, fetchStudents, fetchPendingReviews]);

  const handleApprove = async (id) => {
    try {
      await enrollmentAPI.approve(id);
      toast.success("✅ تم قبول الطالب");
      fetchStudents();
    } catch (error) {
      toast.error("❌ حدث خطأ أثناء القبول");
    }
  };

  const handleReject = async (id) => {
    if (!window.confirm("هل أنت متأكد من رفض هذا الطالب؟")) return;
    try {
      await enrollmentAPI.reject(id);
      toast.info("تم رفض طلب الانضمام");
      fetchStudents();
    } catch (error) {
      toast.error("❌ حدث خطأ");
    }
  };

  const handleBlock = async (id) => {
    if (!window.confirm("هل أنت متأكد من حظر هذا الطالب؟ لن يتمكن من الانضمام مرة أخرى.")) return;
    try {
      await enrollmentAPI.block(id);
      toast.warn("تم حظر الطالب");
      fetchStudents();
    } catch (error) {
      toast.error("❌ حدث خطأ");
    }
  };

  const handleRemove = async (id) => {
    if (!window.confirm("هل أنت متأكد من إزالة هذا الطالب من الدورة؟")) return;
    try {
      await enrollmentAPI.remove(id);
      toast.info("تمت إزالة الطالب من الدورة");
      fetchStudents();
    } catch (error) {
      toast.error("❌ حدث خطأ");
    }
  };

  const handleBulkApprove = async () => {
    if (selectedPending.length === 0) return;
    if (!window.confirm(`هل تريد قبول ${selectedPending.length} طالب دفعةً واحدة؟`)) return;
    try {
      await Promise.all(selectedPending.map((id) => enrollmentAPI.approve(id)));
      toast.success(`✅ تم قبول ${selectedPending.length} طالب`);
      fetchStudents();
    } catch (error) {
      toast.error("❌ حدث خطأ أثناء القبول الجماعي");
    }
  };

  const handleBulkReject = async () => {
    if (selectedPending.length === 0) return;
    if (!window.confirm(`هل تريد رفض ${selectedPending.length} طالب دفعةً واحدة؟`)) return;
    try {
      await Promise.all(selectedPending.map((id) => enrollmentAPI.reject(id)));
      toast.info(`تم رفض ${selectedPending.length} طلب`);
      fetchStudents();
    } catch (error) {
      toast.error("❌ حدث خطأ");
    }
  };

  const toggleSelectPending = (id) => {
    setSelectedPending((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const toggleSelectAll = () => {
    if (selectedPending.length === pendingStudents.length) {
      setSelectedPending([]);
    } else {
      setSelectedPending(pendingStudents.map((s) => s.id));
    }
  };

  const handleCopyCode = () => {
    navigator.clipboard.writeText(course.access_code).then(() => {
      setCopySuccess(true);
      toast.success("تم نسخ كود الدورة!");
      setTimeout(() => setCopySuccess(false), 2000);
    });
  };

  const handleShare = async () => {
    const shareData = {
      title: 'كود الدورة',
      text: course.access_code,
    };

    if (navigator.share) {
      try {
        await navigator.share(shareData);
      } catch (err) {
        console.error("Share failed", err);
      }
    } else {
      handleCopyCode();
      toast.info("تم نسخ الكود (المشاركة غير مدعومة في هذا المتصفح)");
    }
  };

  // Filtered students
  const filteredEnrolled = enrolledStudents.filter((s) =>
    s.full_name?.toLowerCase().includes(studentSearch.toLowerCase()) ||
    s.email?.toLowerCase().includes(studentSearch.toLowerCase())
  );
  const filteredPending = pendingStudents.filter((s) =>
    s.full_name?.toLowerCase().includes(studentSearch.toLowerCase()) ||
    s.email?.toLowerCase().includes(studentSearch.toLowerCase())
  );

  const handleDeleteExam = async (examId) => {
    if (!window.confirm("هل أنت متأكد أنك تريد حذف هذا الامتحان؟")) return;
    try {
      await examsAPI.delete(examId);
      setExams((prev) => prev.filter((e) => e.id !== examId));
      alert("✅ تم حذف الامتحان بنجاح.");
    } catch (error) {
      console.error("❌ فشل في حذف الامتحان:", error);
      alert("❌ فشل في حذف الامتحان.");
    }
  };

  const handleDeleteLesson = async (lessonId) => {
    if (!window.confirm("هل أنت متأكد من حذف هذه المحاضرة؟")) return;
    try {
      await uploadsAPI.deleteLecture(lessonId);
      setLessons((prev) => prev.filter((l) => l.id !== lessonId));
      alert("✅ تم حذف المحاضرة");
    } catch (e) {
      alert("❌ فشل الحذف");
    }
  };

  const getFileIcon = (filePath) => {
    if (!filePath) return <FaFileAlt className="file-icon generic" />;
    if (filePath.endsWith(".pdf")) return <FaFilePdf className="file-icon pdf" />;
    if (filePath.endsWith(".mp4")) return <FaFileVideo className="file-icon video" />;
    return <FaFileAlt className="file-icon generic" />;
  };

  if (loading) return <p className="loading-text">⏳ جارٍ تحميل تفاصيل الدورة...</p>;
  if (!course) return <p className="error-text">❌ الدورة غير موجودة.</p>;

  return (
    <div className="course-details-page">
      {/* Header */}
      <div className="course-header-card">
        <div className="course-header-content">
          <div className="course-icon-large">🎓</div>

          <div className="course-header-main">
            <h1 className="course-title-large">{course.name}</h1>

            <p className="course-meta">
              <span>📅 تم الإنشاء: {new Date(course.created_at).toLocaleDateString("ar-EG")}</span>
              <span className="separator">•</span>
              <span className="access-code-wrapper">
                🔑 كود الدخول: <span className="access-code">{course.access_code}</span>
                <button className="header-share-btn" onClick={handleShare} title="مشاركة الكود" type="button">
                  <FaShareAlt />
                </button>
              </span>
            </p>

            <div className="course-header-actions">
              <button onClick={() => navigate(`/upload/${courseId}`)} className="btn-primary" type="button">
                <FaPlus /> إضافة محاضرة
              </button>
              <button onClick={() => navigate("/add-exam-questions")} className="btn-secondary" type="button">
                <FaPlus /> إنشاء امتحان
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="course-tabs">
        <div className="tabs-scroll">
          <button className={`tab-btn ${activeTab === "overview" ? "active" : ""}`} onClick={() => setActiveTab("overview")} type="button">
            <FaBookOpen /> نظرة عامة
          </button>

          <button className={`tab-btn ${activeTab === "lectures" ? "active" : ""}`} onClick={() => setActiveTab("lectures")} type="button">
            <FaChalkboardTeacher /> المحاضرات <span className="tab-count">{lessons.length}</span>
          </button>

          <button className={`tab-btn ${activeTab === "exams" ? "active" : ""}`} onClick={() => setActiveTab("exams")} type="button">
            <FaClipboardList /> الامتحانات <span className="tab-count">{exams.length}</span>
          </button>

          <button className={`tab-btn ${activeTab === "students" ? "active" : ""}`} onClick={() => setActiveTab("students")} type="button">
            <FaUsers /> الطلاب{" "}
            {pendingStudents.length > 0 ? (
              <span className="badge-notification">{pendingStudents.length}</span>
            ) : (
              <span className="tab-count">{enrolledStudents.length}</span>
            )}
          </button>

          <button className={`tab-btn ${activeTab === "progress" ? "active" : ""}`} onClick={() => setActiveTab("progress")} type="button">
            <FaChartLine /> تقدم الطلاب
          </button>

          <button className={`tab-btn ${activeTab === "reviews" ? "active" : ""}`} onClick={() => setActiveTab("reviews")} type="button">
            <FaExclamationTriangle style={{color: pendingReviews.length > 0 ? '#f59e0b' : undefined}} /> مراجعات معلقة{" "}
            {pendingReviews.length > 0 && <span className="badge-notification">{pendingReviews.length}</span>}
          </button>

          <button className={`tab-btn ${activeTab === "announcements" ? "active" : ""}`} onClick={() => setActiveTab("announcements")} type="button">
            📢 الإعلانات
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="tab-content">
        {/* Overview */}
        {activeTab === "overview" && (
          <div className="overview-section fade-in">
            <div className="description-card">
              <h3>📝 وصف الدورة</h3>
              <p>{course.description || "لا يوجد وصف لهذه الدورة."}</p>
            </div>

            <div className="stats-mini-grid">
              <div className="stat-mini-card">
                <span className="stat-label">عدد المحاضرات</span>
                <span className="stat-number">{lessons.length}</span>
              </div>
              <div className="stat-mini-card">
                <span className="stat-label">عدد الامتحانات</span>
                <span className="stat-number">{exams.length}</span>
              </div>
              <div className="stat-mini-card">
                <span className="stat-label">عدد الطلاب</span>
                <span className="stat-number">{enrolledStudents.length}</span>
              </div>
            </div>
          </div>
        )}

        {/* Lectures */}
        {activeTab === "lectures" && (
          <div className="lectures-list fade-in">
            {lessons.length > 0 ? (
              lessons.map((lesson) => (
                <div key={lesson.id} className="list-item-card">
                  <div className="item-icon">{getFileIcon(lesson.file_path)}</div>

                  <div className="item-details">
                    <h4>{lesson.title}</h4>
                    <p>{lesson.description}</p>
                  </div>

                  <div className="item-actions">
                    <button className="action-btn edit" type="button" onClick={() => navigate(`/edit-lesson/${courseId}/${lesson.id}`)} title="تعديل">
                      <FaEdit /> تعديل
                    </button>

                    {lesson.file_path && (
                      <a
                        href={`${FILE_BASE_URL}${lesson.file_path}?token=${localStorage.getItem("authToken")}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="action-btn ghost"
                        title="عرض"
                      >
                        <FaEye /> عرض
                      </a>
                    )}

                    <button className="action-btn danger" type="button" onClick={() => handleDeleteLesson(lesson.id)} title="حذف">
                      <FaTrash /> حذف
                    </button>
                  </div>
                </div>
              ))
            ) : (
              <p className="empty-msg">لا توجد محاضرات بعد.</p>
            )}
          </div>
        )}

        {/* Exams */}
        {activeTab === "exams" && (
          <div className="exams-list fade-in">
            {exams.length > 0 ? (
              exams.map((exam) => (
                <div key={exam.id} className="list-item-card">
                  <div className="item-icon exam-icon">📝</div>

                  <div className="item-details">
                    <Link to={`/Courses/${courseId}/Exams/${exam.id}`} className="item-link">
                      <h4>{exam.title}</h4>
                    </Link>
                    <p>{exam.description}</p>
                  </div>

                  <div className="item-actions">
                    <button className="action-btn edit" type="button" onClick={() => navigate(`/exams/edit/${exam.id}`)} title="تعديل">
                      <FaEdit /> تعديل
                    </button>

                    <Link to={`/Courses/${courseId}/Exams/${exam.id}`} className="action-btn ghost" title="عرض">
                      <FaEye /> عرض
                    </Link>

                    <button className="action-btn danger" type="button" onClick={() => handleDeleteExam(exam.id)} title="حذف">
                      <FaTrash /> حذف
                    </button>
                  </div>
                </div>
              ))
            ) : (
              <p className="empty-msg">لا توجد امتحانات بعد.</p>
            )}
          </div>
        )}

        {/* Students */}
        {activeTab === "students" && (
          <div className="students-section fade-in">

            {/* Invite Box with Copy Button */}
            <div className="invite-box">
              <div className="invite-box-content">
                <div>
                  <h3>🔑 كود الدورة</h3>
                  <p>شارك هذا الكود مع طلابك للانضمام للدورة</p>
                </div>
                <div className="invite-code-area">
                  <div className="code-display">{course.access_code}</div>
                  <div className="invite-actions">
                    <button className={`copy-code-btn ${copySuccess ? 'copied' : ''}`} onClick={handleCopyCode} type="button">
                      <FaCopy /> {copySuccess ? 'تم النسخ!' : 'نسخ'}
                    </button>
                    <button className="share-code-btn" onClick={handleShare} type="button">
                      <FaShareAlt /> مشاركة
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Stats Cards */}
            <div className="student-stats-grid">
              <div className="student-stat-card total">
                <div className="ssc-icon"><FaUsers /></div>
                <div className="ssc-info">
                  <span className="ssc-value">{enrolledStudents.length}</span>
                  <span className="ssc-label">إجمالي الطلاب</span>
                </div>
              </div>
              <div className="student-stat-card pending">
                <div className="ssc-icon"><FaUserClock /></div>
                <div className="ssc-info">
                  <span className="ssc-value">{pendingStudents.length}</span>
                  <span className="ssc-label">طلبات معلقة</span>
                </div>
              </div>
              <div className="student-stat-card new">
                <div className="ssc-icon"><FaUserCheck /></div>
                <div className="ssc-info">
                  <span className="ssc-value">
                    {enrolledStudents.filter(s => {
                      const weekAgo = new Date(); weekAgo.setDate(weekAgo.getDate() - 7);
                      return new Date(s.approved_at) > weekAgo;
                    }).length}
                  </span>
                  <span className="ssc-label">جدد هذا الأسبوع</span>
                </div>
              </div>
            </div>

            {/* Search & Filter Bar */}
            <div className="students-toolbar">
              <div className="search-box">
                <FaSearch className="search-icon" />
                <input
                  type="text"
                  placeholder="ابحث بالاسم أو الإيميل..."
                  value={studentSearch}
                  onChange={(e) => setStudentSearch(e.target.value)}
                  className="search-input"
                />
              </div>
              <div className="filter-tabs">
                <button className={`filter-tab ${studentFilter === 'all' ? 'active' : ''}`} onClick={() => setStudentFilter('all')} type="button">
                  الكل
                </button>
                <button className={`filter-tab ${studentFilter === 'pending' ? 'active' : ''}`} onClick={() => setStudentFilter('pending')} type="button">
                  معلقون {pendingStudents.length > 0 && <span className="filter-badge">{pendingStudents.length}</span>}
                </button>
                <button className={`filter-tab ${studentFilter === 'enrolled' ? 'active' : ''}`} onClick={() => setStudentFilter('enrolled')} type="button">
                  مسجلون
                </button>
              </div>
            </div>

            {loadingStudents ? (
              <div className="loading-students">
                <div className="spinner-sm"></div>
                <p>⏳ جارٍ تحميل الطلاب...</p>
              </div>
            ) : (
              <>
                {/* Pending Students Section */}
                {(studentFilter === 'all' || studentFilter === 'pending') && (
                  <div className="students-group">
                    <div className="group-header">
                      <h4 className="group-title">
                        <FaUserClock style={{ color: '#d97706' }} />
                        طلبات الانضمام المعلقة
                        <span className="group-count pending-count">{filteredPending.length}</span>
                      </h4>
                      {filteredPending.length > 0 && (
                        <div className="bulk-actions">
                          <button className="bulk-select-btn" onClick={toggleSelectAll} type="button">
                            {selectedPending.length === pendingStudents.length ? <FaCheckSquare /> : <FaSquare />}
                            {selectedPending.length === pendingStudents.length ? ' إلغاء الكل' : ' تحديد الكل'}
                          </button>
                          {selectedPending.length > 0 && (
                            <>
                              <button className="action-btn success" onClick={handleBulkApprove} type="button">
                                <FaCheck /> قبول ({selectedPending.length})
                              </button>
                              <button className="action-btn warn" onClick={handleBulkReject} type="button">
                                <FaTimes /> رفض ({selectedPending.length})
                              </button>
                            </>
                          )}
                        </div>
                      )}
                    </div>

                    {filteredPending.length > 0 ? (
                      <div className="students-list">
                        {filteredPending.map((student) => (
                          <div key={student.id} className={`student-card pending ${selectedPending.includes(student.id) ? 'selected' : ''}`}>
                            <div className="student-select">
                              <input
                                type="checkbox"
                                checked={selectedPending.includes(student.id)}
                                onChange={() => toggleSelectPending(student.id)}
                                className="student-checkbox"
                              />
                            </div>
                            <div className="student-info">
                              <div className="student-avatar pending">{student.full_name?.charAt(0)?.toUpperCase()}</div>
                              <div className="student-details">
                                <h5>{student.full_name}</h5>
                                <span className="student-email">{student.email}</span>
                              </div>
                            </div>
                            <div className="student-actions">
                              <button className="action-btn success" type="button" onClick={() => handleApprove(student.id)}>
                                <FaCheck /> <span className="btn-text">قبول</span>
                              </button>
                              <button className="action-btn warn" type="button" onClick={() => handleReject(student.id)}>
                                <FaTimes /> <span className="btn-text">رفض</span>
                              </button>
                              <button className="action-btn danger" type="button" onClick={() => handleBlock(student.id)}>
                                <FaBan /> <span className="btn-text">حظر</span>
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="empty-state-sm">
                        <FaUserClock className="empty-icon" />
                        <p>لا توجد طلبات انضمام معلقة</p>
                      </div>
                    )}
                  </div>
                )}

                {/* Enrolled Students Section - Table Layout */}
                {(studentFilter === 'all' || studentFilter === 'enrolled') && (
                  <div className="students-group">
                    <div className="group-header">
                      <h4 className="group-title">
                        <FaUserCheck style={{ color: '#16a34a' }} />
                        الطلاب المسجلون
                        <span className="group-count enrolled-count">{filteredEnrolled.length}</span>
                      </h4>
                    </div>

                    {filteredEnrolled.length > 0 ? (
                      <div className="enrolled-table-wrapper">
                        <table className="enrolled-table">
                          <thead>
                            <tr>
                              <th>#</th>
                              <th>الطالب</th>
                              <th className="hide-mobile">الإيميل</th>
                              <th className="hide-mobile">تاريخ الانضمام</th>
                              <th>الإجراءات</th>
                            </tr>
                          </thead>
                          <tbody>
                            {filteredEnrolled.map((student, idx) => (
                              <tr key={student.id}>
                                <td className="row-num">{idx + 1}</td>
                                <td>
                                  <div className="table-student-info">
                                    <div className="student-avatar active">{student.full_name?.charAt(0)?.toUpperCase()}</div>
                                    <div>
                                      <div className="student-name">{student.full_name}</div>
                                      <div className="show-mobile student-email-sm">{student.email}</div>
                                    </div>
                                  </div>
                                </td>
                                <td className="hide-mobile student-email-td">{student.email}</td>
                                <td className="hide-mobile join-date">
                                  {student.approved_at
                                    ? new Date(student.approved_at).toLocaleDateString("ar-EG", { day: 'numeric', month: 'short', year: 'numeric' })
                                    : '—'}
                                </td>
                                <td>
                                  <div className="table-actions">
                                    <button
                                      className="action-btn info"
                                      type="button"
                                      onClick={() => setReviewStudent({ id: student.id, name: student.full_name || student.email })}
                                      title="مراجعة الامتحانات"
                                    >
                                      <FaChartLine /> <span className="btn-text">الامتحانات</span>
                                    </button>
                                    <button className="action-btn warn" type="button" onClick={() => handleRemove(student.id)} title="إزالة من الدورة">
                                      <FaUserTimes /> <span className="btn-text">إزالة</span>
                                    </button>
                                    <button className="action-btn danger" type="button" onClick={() => handleBlock(student.id)} title="حظر">
                                      <FaBan /> <span className="btn-text">حظر</span>
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <div className="empty-state-sm">
                        <FaUsers className="empty-icon" />
                        <p>{studentSearch ? 'لا توجد نتائج للبحث' : 'لا يوجد طلاب مسجلون في هذه الدورة بعد'}</p>
                      </div>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* Pending Reviews */}
        {activeTab === "reviews" && (
          <div className="fade-in">
            <h3 style={{ marginBottom: "1rem", display: "flex", alignItems: "center", gap: "8px" }}>
              <FaExclamationTriangle style={{ color: "#f59e0b" }} />
              إجابات مقالية تنتظر التصحيح
            </h3>

            {loadingReviews ? (
              <div style={{ textAlign: "center", padding: "2rem" }}>
                <div className="spinner-sm" />
                <p>⏳ جارٍ تحميل قائمة المراجعات...</p>
              </div>
            ) : pendingReviews.length === 0 ? (
              <div className="empty-state-sm" style={{ padding: "3rem", textAlign: "center" }}>
                <FaCheckSquare style={{ fontSize: "2.5rem", color: "#10b981", marginBottom: "1rem" }} />
                <p style={{ color: "#6b7280", fontWeight: 500 }}>🎉 لا توجد مراجعات معلقة، أحسنت!</p>
              </div>
            ) : (
              <div className="exams-list">
                {pendingReviews.map((review) => (
                  <div key={review.attempt_id} className="list-item-card" style={{ borderRight: "4px solid #f59e0b" }}>
                    <div className="item-icon" style={{ background: "#fef3c7", color: "#d97706", borderRadius: "12px", padding: "10px", fontSize: "1.4rem" }}>
                      ✍️
                    </div>

                    <div className="item-details">
                      <h4 style={{ margin: 0 }}>{review.student_name || review.student_email}</h4>
                      <p style={{ margin: "4px 0 0", color: "#6b7280", fontSize: "0.85rem" }}>
                        📝 {review.exam_title} &nbsp;•&nbsp;
                        🕒 {new Date(review.submitted_at).toLocaleString("ar-EG", { dateStyle: "medium", timeStyle: "short" })}
                      </p>
                    </div>

                    <div className="item-actions">
                      <button
                        className="action-btn edit"
                        type="button"
                        onClick={() => setReviewStudent({ id: review.student_id, name: review.student_name || review.student_email })}
                      >
                        <FaPen /> صحّح الآن
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Student Progress */}
        {activeTab === "progress" && (
          <div className="progress-section fade-in">
            <StudentProgress courseId={courseId} />
          </div>
        )}

        {/* Announcements */}
        {activeTab === "announcements" && (
          <div className="fade-in">
            <Announcements courseId={courseId} />
          </div>
        )}
      </div>

      {/* Exam Review Modal */}
      {reviewStudent && (
        <ExamReviewModal
          courseId={courseId}
          studentId={reviewStudent.id}
          studentName={reviewStudent.name}
          onClose={() => {
            setReviewStudent(null);
            if (activeTab === "reviews") fetchPendingReviews();
          }}
        />
      )}
    </div>
  );
};

export default CourseDetails;
